import { z } from "zod";

// ADR-0089: reply targets per priority, counted in the desk's business hours.

/** Whether the runtime knows an IANA time zone name. */
export function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** A reply target in minutes: at least 15 minutes, at most 30 business days of 8 hours. */
const TargetSchema = z
  .number("Enter each reply target in hours, such as 4 or 0.5.")
  .int()
  .min(15, "Reply targets must be at least 0.25 hours.")
  .max(14_400, "Reply targets can be at most 240 hours.");

export const ServiceLevelsSchema = z
  .strictObject({
    timeZone: z.string().min(1).max(64).refine(isTimeZone, "Choose a valid time zone."),
    /** Business days, 0 for Sunday through 6 for Saturday. */
    days: z
      .array(z.number().int().min(0).max(6))
      .min(1, "Choose at least one business day.")
      .max(7)
      .transform((days) => [...new Set(days)].sort((left, right) => left - right)),
    startMinute: z.number("Enter opening and closing times like 08:00.").int().min(0).max(1_439),
    endMinute: z.number("Enter opening and closing times like 08:00.").int().min(1).max(1_440),
    targets: z.strictObject({
      urgent: TargetSchema,
      high: TargetSchema,
      normal: TargetSchema,
      low: TargetSchema,
    }),
  })
  .refine((levels) => levels.endMinute > levels.startMinute, {
    message: "Business hours must end after they start.",
    path: ["endMinute"],
  });
export type ServiceLevels = z.infer<typeof ServiceLevelsSchema>;

export const DEFAULT_SERVICE_LEVELS: ServiceLevels = {
  timeZone: "America/Los_Angeles",
  days: [1, 2, 3, 4, 5],
  startMinute: 480,
  endMinute: 1_020,
  targets: { urgent: 60, high: 240, normal: 480, low: 960 },
};
